import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { 
  Sun, Moon, LayoutGrid, BarChart2, User, LogOut, 
  Settings, Refrigerator, CheckCircle2, Wifi, WifiOff, 
  Mail, Download, X, Share, Smartphone, HelpCircle, Search,
  ChevronDown, ShieldCheck
} from 'lucide-react';
import { LoginStreakBadge } from './LoginStreakBadge';

export const DashboardHeader = ({ 
  user, isConnected, usePolling, onLogout, onReplayTutorial,
  interests, isInstallable, isInstalled, isIOS, promptInstall,
  dragOverAchieve
}) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const searchInputRef = useRef(null);

  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    const q = query.toLowerCase();
    const liveMatches = interests.filter(i => 
      i.title?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q)
    );

    if (liveMatches.length > 0) {
      setSearchResults({ type: 'interests', items: liveMatches });
      return;
    }

    try {
      const api = (await import('../../lib/api')).default;
      const res = await api.get('/achievements');
      const achievements = res.data || [];
      const achievementMatches = achievements.filter(a =>
        a.title?.toLowerCase().includes(q) || a.description?.toLowerCase().includes(q)
      );

      if (achievementMatches.length > 0) {
        setSearchResults({ type: 'achievements', items: achievementMatches });
      } else {
        setSearchResults({ type: 'none', items: [] });
      }
    } catch {
      setSearchResults({ type: 'none', items: [] });
    }
  };

  return (
    <header className="flex items-center justify-between p-3 border-b border-border relative">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
          <LayoutGrid className="w-4 h-4 text-primary-foreground" />
        </div>
        <span className="text-lg font-bold font-['Manrope'] hidden sm:block">Brioritizer</span>
      </div>

      <div 
        onClick={() => navigate('/achievements')}
        className={`absolute left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 border-dashed cursor-pointer
          transition-all duration-300 hover:scale-105
          ${dragOverAchieve 
            ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/50 scale-105' 
            : 'border-blue-300 dark:border-blue-700 bg-blue-50/80 dark:bg-blue-950/50 hover:border-blue-400'
          }`}
        data-testid="achievement-store"
      >
        <Refrigerator className="w-5 h-5 text-blue-500" />
        <span className="text-xs font-medium text-blue-600 dark:text-blue-400 hidden sm:block">Achievements</span>
      </div>

      <div className="flex items-center gap-2">
        <LoginStreakBadge streak={user?.login_streak} bestStreak={user?.best_streak} />

        <Popover open={showSearch} onOpenChange={(open) => { setShowSearch(open); if (!open) { setSearchQuery(''); setSearchResults(null); } }}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="search-btn">
              <Search className="w-5 h-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3" align="end">
            <div className="space-y-3">
              <Input
                ref={searchInputRef}
                placeholder="Search interests..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
                data-testid="search-input"
              />
              {searchResults && (
                <div className="max-h-64 overflow-y-auto">
                  {searchResults.type === 'interests' && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground px-1">Found in your interests</p>
                      {searchResults.items.map(item => (
                        <button
                          key={item.interest_id}
                          className="w-full text-left px-2 py-1.5 rounded-md hover:bg-muted text-sm flex items-center gap-2"
                          onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults(null); }}
                          data-testid={`search-result-${item.interest_id}`}
                        >
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                            backgroundColor: item.quadrant === 'q1' ? '#22c55e' : item.quadrant === 'q4' ? '#ef4444' : '#f59e0b'
                          }} />
                          <span className="truncate">{item.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchResults.type === 'achievements' && (
                    <div className="space-y-2">
                      <div className="px-1 py-2 text-sm text-muted-foreground">
                        Not found in your live interests, but found in your{' '}
                        <a
                          href="/achievements"
                          onClick={(e) => { e.preventDefault(); setShowSearch(false); navigate('/achievements'); }}
                          className="text-primary hover:underline font-medium"
                        >Achievement Store</a>:
                      </div>
                      {searchResults.items.map(item => (
                        <div
                          key={item.achievement_id}
                          className="px-2 py-1.5 rounded-md bg-blue-50 dark:bg-blue-950/30 text-sm flex items-center gap-2"
                          data-testid={`search-achievement-${item.achievement_id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                          <span className="truncate">{item.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.type === 'none' && (
                    <p className="text-sm text-muted-foreground text-center py-3" data-testid="search-no-results">
                      No matches found in interests or achievements.
                    </p>
                  )}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        <div 
          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            isConnected 
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400' 
              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400'
          }`}
          title={isConnected ? (usePolling ? 'Cloud sync active' : 'Real-time sync active') : 'Connecting...'}
          data-testid="sync-indicator"
        >
          {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
          <span className="hidden sm:inline">{isConnected ? 'Synced' : 'Syncing'}</span>
        </div>
        
        <Button variant="ghost" size="icon" onClick={() => navigate('/analytics')} data-testid="analytics-btn">
          <BarChart2 className="w-5 h-5" />
        </Button>
        <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
              <User className="w-5 h-5" />
              <span className="hidden sm:block">{user?.name?.split(' ')[0]}</span>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="font-medium">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/achievements')} data-testid="achievements-menu-item">
              <Refrigerator className="w-4 h-4 mr-2" />
              Achievement Store
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/analytics')} data-testid="analytics-menu-item">
              <BarChart2 className="w-4 h-4 mr-2" />
              Analytics
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem asChild data-testid="support-menu-item">
              <a href="mailto:brian@brioritize.com">
                <Mail className="w-4 h-4 mr-2" />
                Support
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onReplayTutorial} data-testid="tutorial-menu-item">
              <HelpCircle className="w-4 h-4 mr-2" />
              Tutorial
            </DropdownMenuItem>
            {(isInstallable || isIOS) && !isInstalled && (
              <DropdownMenuItem 
                onClick={async () => {
                  if (isIOS) {
                    toast.info('Tap the Share button in Safari, then "Add to Home Screen"');
                  } else if (isInstallable) {
                    const success = await promptInstall();
                    if (success) {
                      toast.success('App installed successfully!');
                    }
                  }
                }}
                data-testid="install-menu-item"
              >
                <Download className="w-4 h-4 mr-2" />
                Install App
              </DropdownMenuItem>
            )}
            {user?.is_admin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-menu-item" className="text-rose-600 dark:text-rose-400">
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Admin Dashboard
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} data-testid="logout-menu-item">
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
